<template>
  <div class="store-context" :class="{ 'is-open': isOpen }">
    <button
      class="store-pill"
      type="button"
      @click="handlePillClick"
      :class="{ 'multi-store': multiStoreUser, 'has-actions': totalActions > 0, 'is-clickable': isClickable }"
    >
      <span class="store-name">{{ currentStoreName }}</span>
      <span v-if="totalActions > 0" class="action-badge">{{ totalActions }}</span>
      <svg v-if="multiStoreUser || totalActions > 0" class="pill-chevron" :class="{ rotated: isOpen }" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>

    <transition name="menu-fade">
      <div v-if="isOpen && (multiStoreUser || totalActions > 0)" class="store-menu">
        <!-- Stores with pending actions first -->
        <div class="menu-section" v-if="storesWithActions.length > 0">
          <div class="section-label">Action Required</div>
          <a
            v-for="store in storesWithActions"
            :key="'action-' + store.id"
            class="store-option store-option-action"
            :class="{ current: store.is_current }"
            href="#"
            @click.prevent="switchStore(store.id)"
          >
            <div class="option-main">
              <span class="option-name">{{ store.name }}</span>
              <span v-if="store.is_current" class="current-tag">You're here</span>
            </div>
            <div class="option-actions">
              <button
                v-if="store.pending_approval > 0"
                class="action-tag action-approve action-tag-clickable"
                :title="'Click to view ' + store.pending_approval + ' transfer(s) needing approval'"
                @click.stop="goToAction(store, 'approve')"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {{ store.pending_approval }} to approve
              </button>
              <button
                v-if="store.ready_to_receive > 0"
                class="action-tag action-receive action-tag-clickable"
                :title="'Click to receive ' + store.ready_to_receive + ' transfer(s)'"
                @click.stop="goToAction(store, 'receive')"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {{ store.ready_to_receive }} to receive
              </button>
            </div>
          </a>
        </div>

        <!-- All stores (only for multi-store users) -->
        <div class="menu-section" v-if="multiStoreUser">
          <div class="section-label" v-if="storesWithActions.length > 0">All Stores</div>
          <a
            v-for="store in storesWithoutActions"
            :key="store.id"
            class="store-option"
            :class="{ current: store.is_current }"
            href="#"
            @click.prevent="switchStore(store.id)"
          >
            <div class="option-main">
              <span class="option-name">{{ store.name }}</span>
              <span v-if="store.is_current" class="current-tag">You're here</span>
            </div>
            <span class="option-role">{{ store.role }}</span>
          </a>
        </div>

        <!-- Single Store User Actions (when not multi-store but has actions) -->
        <div class="menu-section" v-if="!multiStoreUser && totalActions > 0">
          <div class="section-label">Your Pending Actions</div>
          <div class="single-store-actions">
            <a
              v-if="currentStoreActions.pending_approval > 0"
              href="/stock_transfers#approve-outgoing"
              class="action-link action-link-approve"
            >
              <div class="action-link-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div class="action-link-content">
                <span class="action-link-count">{{ currentStoreActions.pending_approval }}</span>
                <span class="action-link-label">Transfer{{ currentStoreActions.pending_approval > 1 ? 's' : '' }} to Approve</span>
              </div>
              <svg class="action-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </a>
            <a
              v-if="currentStoreActions.ready_to_receive > 0"
              href="/stock_transfers#receive-incoming"
              class="action-link action-link-receive"
            >
              <div class="action-link-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <div class="action-link-content">
                <span class="action-link-count">{{ currentStoreActions.ready_to_receive }}</span>
                <span class="action-link-label">Transfer{{ currentStoreActions.ready_to_receive > 1 ? 's' : '' }} to Receive</span>
              </div>
              <svg class="action-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </a>
          </div>
        </div>

        <div class="menu-divider" v-if="multiStoreUser || totalActions > 0"></div>

        <!-- Quick Links -->
        <div class="menu-section">
          <a v-if="canViewCrossStore" class="menu-link menu-link-highlight" href="/dashboard/aggregate">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span>All Stores Dashboard</span>
          </a>
          <a class="menu-link" href="/stock_transfers">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="17 1 21 5 17 9"></polyline>
              <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
              <polyline points="7 23 3 19 7 15"></polyline>
              <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
            </svg>
            <span>View All Transfers</span>
          </a>
          <a v-if="canViewCrossStore" class="menu-link" href="/stock_transfers/cross_store_stock">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
            <span>Cross-Store Stock</span>
          </a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import http from "../common/http";

export default {
  name: "StoreSwitcher",
  data: function() {
    return {
      isOpen: false,
      stores: [],
      currentStore: null,
      multiStoreUser: false,
      canViewCrossStore: false,
      isLoading: false,
    };
  },
  computed: {
    currentStoreName: function() {
      return this.currentStore ? this.currentStore.name : 'Store';
    },
    totalActions: function() {
      var total = 0;
      for (var i = 0; i < this.stores.length; i++) {
        total += (this.stores[i].pending_approval || 0) + (this.stores[i].ready_to_receive || 0);
      }
      return total;
    },
    storesWithActions: function() {
      return this.stores.filter(function(s) {
        return (s.pending_approval || 0) + (s.ready_to_receive || 0) > 0;
      });
    },
    storesWithoutActions: function() {
      return this.stores.filter(function(s) {
        return (s.pending_approval || 0) + (s.ready_to_receive || 0) === 0;
      });
    },
    isClickable: function() {
      return this.multiStoreUser || this.totalActions > 0;
    },
    currentStoreActions: function() {
      if (!this.currentStore) return { pending_approval: 0, ready_to_receive: 0 };
      return {
        pending_approval: this.currentStore.pending_approval || 0,
        ready_to_receive: this.currentStore.ready_to_receive || 0
      };
    }
  },
  mounted: function() {
    // Load preloaded data instantly (no API wait needed)
    this.loadPreloadedData();
    // If preload failed or empty, fetch from API
    if (this.stores.length === 0) {
      this.fetchStoresQuietly();
    }
    document.addEventListener("click", this.closeDropdown);
    // Listen for transfer updates to refresh counts
    window.addEventListener("transfer-updated", this.fetchStores);
  },
  beforeDestroy: function() {
    document.removeEventListener("click", this.closeDropdown);
    window.removeEventListener("transfer-updated", this.fetchStores);
  },
  methods: {
    // Load preloaded data from data attribute (instant, no wait)
    loadPreloadedData: function() {
      var el = document.getElementById('store-switcher');
      if (el && el.dataset.preload) {
        try {
          var data = JSON.parse(el.dataset.preload);
          if (data.stores) {
            this.stores = data.stores;
            this.currentStore = this.stores.find(function(s) { return s.is_current; });
            this.multiStoreUser = data.multi_store_user || false;
            if (data.permissions) {
              this.canViewCrossStore = data.permissions.can_view_cross_store || false;
            }
          }
        } catch (e) {
          console.error("Error parsing preloaded store data:", e);
        }
      }
    },
    // Fetch fresh data quietly (for action counts) without blocking UI
    fetchStoresQuietly: function() {
      var self = this;
      http.get("/store_switcher.json")
        .then(function(response) {
          // Only update if we got valid data
          if (response.data.stores) {
            self.stores = response.data.stores;
            self.currentStore = self.stores.find(function(s) { return s.is_current; });
            self.multiStoreUser = response.data.multi_store_user;
            if (response.data.permissions) {
              self.canViewCrossStore = response.data.permissions.can_view_cross_store;
            }
          }
        })
        .catch(function(error) {
          // Silent fail - we already have preloaded data
          console.warn("Background store refresh failed:", error);
        });
    },
    fetchStores: function() {
      this.fetchStoresQuietly();
    },
    handlePillClick: function(event) {
      event.stopPropagation();
      // If clickable (multi-store or has actions), toggle the dropdown
      if (this.isClickable) {
        this.isOpen = !this.isOpen;
        // Fetch fresh data when opening dropdown (lazy load)
        if (this.isOpen) {
          this.fetchStoresQuietly();
        }
      }
    },
    toggleDropdown: function(event) {
      if (!this.isClickable) return;
      event.stopPropagation();
      this.isOpen = !this.isOpen;
    },
    closeDropdown: function(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
    switchStore: function(storeId, redirectUrl) {
      var self = this;
      if (this.isLoading) return;
      var targetStore = this.stores.find(function(s) { return s.id === storeId; });
      if (targetStore && targetStore.is_current) {
        this.isOpen = false;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
        return;
      }
      this.isLoading = true;
      // Release camera streams first — they proxy through this origin and would
      // otherwise saturate the browser's per-host connection limit, blocking this XHR.
      window.dispatchEvent(new CustomEvent("camera-popup:release"));
      http.post("/store_switcher/switch.json", { store_id: storeId })
        .then(function(response) {
          if (response.data.success) {
            if (redirectUrl) {
              window.location.href = redirectUrl;
            } else {
              window.location.reload();
            }
          }
        })
        .catch(function(error) {
          console.error("Error switching store:", error);
          // Switch failed — we're staying on this page, so bring the streams back.
          window.dispatchEvent(new CustomEvent("camera-popup:resume"));
        })
        .finally(function() {
          self.isLoading = false;
        });
    },
    goToAction: function(store, actionType) {
      // Navigate to stock transfers page after switching to the store
      var redirectUrl = "/stock_transfers";
      if (actionType === "approve") {
        redirectUrl += "#approve-outgoing";
      } else if (actionType === "receive") {
        redirectUrl += "#receive-incoming";
      }
      this.switchStore(store.id, redirectUrl);
    }
  },
};
</script>

<style scoped>
.store-context {
  position: relative;
  z-index: 99999;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif;
}

/* The Pill */
.store-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #e5e5e7;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #1d1d1f;
  cursor: default;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.store-pill.multi-store,
.store-pill.is-clickable {
  cursor: pointer;
}

.store-pill.multi-store:hover {
  background: #f5f5f7;
  border-color: #d1d1d6;
}

.store-pill.has-actions {
  border-color: #FF9500;
  background: linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 100%);
}

.store-context.is-open .store-pill {
  border-color: #007AFF;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.store-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-badge {
  background: #FF3B30;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.pill-chevron {
  color: #86868b;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.pill-chevron.rotated {
  transform: rotate(180deg);
}

/* The Menu */
.store-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  z-index: 999999;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.15s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Sections */
.menu-section {
  padding: 8px;
}

.section-label {
  font-size: 11px;
  font-weight: 600;
  color: #86868b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px 6px;
}

.menu-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 4px 12px;
}

/* Store Options */
.store-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: #1d1d1f;
  transition: background 0.15s ease;
  gap: 12px;
}

.store-option:hover {
  background: #f5f5f7;
}

.store-option.current {
  background: rgba(0, 122, 255, 0.06);
}

.store-option-action {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: #FFFBF5;
  border: 1px solid #FFE4BC;
}

.store-option-action:hover {
  background: #FFF5E6;
}

.store-option-action.current {
  background: #F0F9FF;
  border-color: #BAE6FD;
}

.option-main {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.option-name {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
}

.current-tag {
  font-size: 10px;
  font-weight: 500;
  color: #007AFF;
  background: rgba(0, 122, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.option-role {
  font-size: 11px;
  color: #86868b;
  text-transform: capitalize;
}

/* Action Tags */
.option-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.action-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.action-tag-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-approve {
  background: #FEF3C7;
  color: #92400E;
}

.action-approve.action-tag-clickable:hover {
  background: #FDE68A;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(146, 64, 14, 0.2);
}

.action-receive {
  background: #D1FAE5;
  color: #065F46;
}

.action-receive.action-tag-clickable:hover {
  background: #A7F3D0;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(6, 95, 70, 0.2);
}

/* Menu Links */
.menu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.menu-link:hover {
  background: #f5f5f7;
  color: #1d1d1f;
}

.menu-link-highlight {
  background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%);
  color: #4F46E5;
  border: 1px solid #C7D2FE;
}

.menu-link-highlight:hover {
  background: linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%);
  color: #4338CA;
}

.menu-link svg {
  flex-shrink: 0;
}

/* Single Store Action Links */
.single-store-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.action-link-approve {
  background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
  border: 1px solid #FDE68A;
}

.action-link-approve:hover {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  transform: translateX(2px);
}

.action-link-receive {
  background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%);
  border: 1px solid #A7F3D0;
}

.action-link-receive:hover {
  background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
  transform: translateX(2px);
}

.action-link-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-link-approve .action-link-icon {
  background: #FEF3C7;
  color: #92400E;
}

.action-link-receive .action-link-icon {
  background: #D1FAE5;
  color: #065F46;
}

.action-link-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.action-link-count {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.action-link-approve .action-link-count {
  color: #92400E;
}

.action-link-receive .action-link-count {
  color: #065F46;
}

.action-link-label {
  font-size: 12px;
  font-weight: 500;
}

.action-link-approve .action-link-label {
  color: #B45309;
}

.action-link-receive .action-link-label {
  color: #047857;
}

.action-link-arrow {
  color: #9CA3AF;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.action-link:hover .action-link-arrow {
  transform: translateX(3px);
  color: #6B7280;
}
</style>
